import * as _ from 'lodash';
import type { FunctionComponent } from 'react';

import Accordion from '../Accordion';
import { Badge, BadgeColors } from '../Badge';

import Loading from './DetailsPanelLoading';

import styles from './DetailsPanel.module.less';

type Props = {
  /**
   * A unique ID for the accordion component.
   */
  id: string;
  /**
   * A label for the click target of the accordion.
   */
  target: string;
  /**
   * Target can optionally display a small count beside the target.
   */
  targetCount?: number;
  /**
   * If the accordion is initially open
   */
  isInitiallyOpen?: boolean;
  /**
   * If the loading UI should be displayed.
   */
  isLoading?: boolean;
  /**
   * Optionally you can decide how many "ghost rows" are displayed while the panel is loading. (@default 6)
   */
  loadingRowCount?: number;
};

/**
 * DetailsPanel is an accordion panel which displays a list of rows,
 * where each row contains a left-aligned "label" and a right-aligned
 * "content", which may contain any arbitrary content.
 *
 * @example
 * <DetailsPanel
 *   id="vuln-details"
 *   target="Details"
 *   isLoading={asyncStatus.isLoading}
 * >
 *   <div>Some details! Any `children` will work here!</div>
 * </DetailsPanel>
 */
const DetailsPanel: FunctionComponent<Props> = ({
  id,
  target,
  children,
  isInitiallyOpen = true,
  isLoading,
  loadingRowCount = 6,
  targetCount,
}) => (
  <div className={styles.panel}>
    <Accordion id={id} isInitiallyOpen={isInitiallyOpen}>
      <Accordion.Target>
        {target}{!_.isNil(targetCount) && (
          <span className="margin-left-xs">
            <Badge color={BadgeColors.LightGray}>
              {targetCount}
            </Badge>
          </span>
        )}
      </Accordion.Target>
      <Accordion.Body>
        <div className={styles.body}>
          {isLoading ? <Loading rowCount={loadingRowCount} /> : children}
        </div>
      </Accordion.Body>
    </Accordion>
  </div>
);

export { default as DetailsPanelLink } from './DetailsPanelLink';
export { default as DetailsPanelList } from './DetailsPanelList';

export default DetailsPanel;
